import { useEffect, useMemo, useState } from "react";
import {
	getCertificationSignatureTypeName,
} from "views/Eventos/utils/certification";
import {
	requestGetPastEventCertificationBatches,
	requestGetPastEventCertificationConfig,
	requestGetPastEventCertificationFile,
	requestGetPastEventCertificationStudents,
	requestGetPastEventCertificationSummary,
	requestPastEventCertificationGlobalReprocess,
	requestPastEventCertificationStudentReprocess,
} from "services/events/aboutEvents";
import {
	DashboardCard,
	SectionShell,
	TopSecondaryStack,
} from "./styles";
import OperationalHeader from "./components/OperationalHeader";
import MetricsStrip from "./components/MetricsStrip";
import AttentionPanel from "./components/AttentionPanel";
import ConfigSnapshot from "./components/ConfigSnapshot";
import StudentsSection from "./components/StudentsSection";
import BatchAuditSection from "./components/BatchAuditSection";
import PastEventCertificationEditorDialog from "./components/PastEventCertificationEditorDialog";

const STUDENT_FILTERS = [
	{ code: "", label: "Todos" },
	{ code: "ERROR", label: "Con error" },
	{ code: "PENDING", label: "Pendientes" },
	{ code: "ISSUED", label: "Emitidos" },
	{ code: "NOT_ELIGIBLE", label: "No elegibles" },
];

const STATUS_TEXT = {
	PENDING: "Pendiente",
	PENDING_INITIAL: "Pendiente",
	PENDING_PDF: "Pendiente PDF",
	PENDING_REPROCESS: "Pendiente",
	PROCESSING: "En proceso",
	ISSUED: "Emitido",
	NOT_ELIGIBLE: "No elegible",
	FAILED: "Error",
	COMPLETED: "Completado",
	COMPLETED_WITH_ERRORS: "Completado con errores",
};

const STATUS_VARIANT = {
	PENDING: "warning",
	PENDING_INITIAL: "warning",
	PENDING_PDF: "warning",
	PENDING_REPROCESS: "warning",
	PROCESSING: "warning",
	ISSUED: "success",
	NOT_ELIGIBLE: "neutral",
	FAILED: "danger",
	COMPLETED: "success",
	COMPLETED_WITH_ERRORS: "danger",
};

const ELIGIBILITY_TEXT = {
	UNKNOWN: "No determinado",
	ELIGIBLE: "Elegible",
	NOT_ELIGIBLE: "No elegible",
};

const ELIGIBILITY_VARIANT = {
	UNKNOWN: "neutral",
	ELIGIBLE: "success",
	NOT_ELIGIBLE: "neutral",
};

const BATCH_TYPE_TEXT = {
	INITIAL: "Inicial",
	REPROCESS_GLOBAL: "Reproceso global",
	REPROCESS_INDIVIDUAL: "Reproceso individual",
};

const ERROR_TEXT = {
	MISSING_SIGNATURE: "Falta una firma valida para emitir la constancia.",
	STUDENT_NOT_ELIGIBLE:
		"El alumno no cumple con el minimo de asistencia requerido.",
	PDF_GENERATION_ERROR: "Ocurrio un problema al generar el PDF.",
	STORAGE_ERROR: "Ocurrio un problema al guardar la constancia.",
	WORKER_ERROR: "Ocurrio un error en el proceso automatico.",
};

const POSITION_LABELS = {
	LEFT: "IZQ",
	CENTER: "CEN",
	RIGHT: "DER",
};

const STUDENT_STATUS_PRIORITY = {
	FAILED: 0,
	PENDING_REPROCESS: 1,
	PENDING_PDF: 2,
	PENDING_INITIAL: 3,
	PROCESSING: 4,
	ISSUED: 5,
	NOT_ELIGIBLE: 6,
};

const formatDate = timestamp => {
	if (!timestamp) return "-";
	try {
		return new Date(timestamp).toLocaleString("es-PE");
	} catch (err) {
		return "-";
	}
};

const getStatusText = status => STATUS_TEXT[status] || status || "-";

const getStatusVariant = status => STATUS_VARIANT[status] || "neutral";

const getEligibilityText = status => ELIGIBILITY_TEXT[status] || status || "-";

const getEligibilityVariant = status =>
	ELIGIBILITY_VARIANT[status] || "neutral";

const getBatchTypeText = batchType => BATCH_TYPE_TEXT[batchType] || batchType || "-";

const getErrorText = (code, fallback) =>
	ERROR_TEXT[code] || fallback || "-";

const getMetricTone = metric => {
	const value = Number(metric?.value) || 0;
	if (metric?.key === "failed") return value > 0 ? "critical" : "neutral";
	if (metric?.key === "pending") return value > 0 ? "attention" : "neutral";
	if (metric?.key === "issued") return value > 0 ? "healthy" : "neutral";
	if (metric?.key === "notEligible") return value > 0 ? "info" : "neutral";
	return "neutral";
};

const getCurrentConfigRevision = summary =>
	typeof summary?.currentConfigRevision !== "undefined"
		? summary.currentConfigRevision
		: null;

const isActiveBatchStatus = status =>
	status === "PENDING" || status === "PROCESSING";

const getReprocessState = summary => {
	const currentBatch = summary?.currentBatch || {};
	const isGlobalReprocess = currentBatch.batchType === "REPROCESS_GLOBAL";
	const hasPendingReprocess = Boolean(summary?.hasPendingReprocess);
	const isActiveGlobalReprocess =
		hasPendingReprocess &&
		isGlobalReprocess &&
		isActiveBatchStatus(currentBatch.status);
	const hasCompletedGlobalReprocessWithErrors =
		hasPendingReprocess &&
		isGlobalReprocess &&
		currentBatch.status === "COMPLETED_WITH_ERRORS";

	return {
		isActiveGlobalReprocess,
		hasCompletedGlobalReprocessWithErrors,
	};
};

const getFilterCount = (filterCode, counters = {}) => {
	if (!filterCode)
		return (
			Number(counters.totalEnrolled) ||
			Number(counters.issued) +
				Number(counters.pending) +
				Number(counters.notEligible) +
				Number(counters.failed) ||
			0
		);
	if (filterCode === "ISSUED") return Number(counters.issued) || 0;
	if (filterCode === "PENDING") return Number(counters.pending) || 0;
	if (filterCode === "NOT_ELIGIBLE") return Number(counters.notEligible) || 0;
	if (filterCode === "ERROR") return Number(counters.failed) || 0;
	return 0;
};

const getLastExecutionText = summary => {
	const timestamp =
		summary?.currentBatch?.requestedAt ||
		summary?.lastRequestedAt ||
		summary?.lastProcessedAt ||
		null;
	return formatDate(timestamp);
};

const buildAttentionItems = summary => {
	const counters = summary?.counters || {};
	const currentBatch = summary?.currentBatch || {};
	const items = [];
	const hasPendingReprocess = Boolean(summary?.hasPendingReprocess);
	const isProcessingBatch = currentBatch.status === "PROCESSING";
	const isPendingBatch = currentBatch.status === "PENDING";
	const isGlobalReprocess = currentBatch.batchType === "REPROCESS_GLOBAL";
	const failedCount =
		Number(currentBatch.failedCount) || Number(counters.failed) || 0;
	const processingCount = Number(counters.processing) || 0;

	if (!summary) return items;

	if (
		hasPendingReprocess &&
		isGlobalReprocess &&
		(isPendingBatch || isProcessingBatch)
	) {
		items.push({
			id: "global-reprocess",
			tone: "warning",
			title: isProcessingBatch
				? "Reproceso global en curso"
				: "Reproceso global pendiente",
			description: isProcessingBatch
				? "Hay un lote operativo ejecutandose."
				: "Existe un lote global en cola.",
			pill: isProcessingBatch ? "En proceso" : "En cola",
			pillVariant: "warning",
		});
	}

	if (currentBatch.status === "COMPLETED_WITH_ERRORS") {
		items.push({
			id: "batch-completed-with-errors",
			tone: "critical",
			title: "Ultimo lote con errores",
			description:
				failedCount > 0
					? `${failedCount} participante(s) requieren revision.`
					: "Revisa el detalle del lote y los participantes afectados.",
			pill: "Revisar",
			pillVariant: "danger",
		});
	}

	if (currentBatch.status === "FAILED") {
		items.push({
			id: "batch-failed",
			tone: "critical",
			title: "Lote fallido",
			description:
				currentBatch.errorMessage ||
				"El proceso no concluyo correctamente y requiere revision.",
			pill: "Critico",
			pillVariant: "danger",
		});
	}

	if (
		processingCount > 0 &&
		!items.some(item => item.id === "global-reprocess")
	) {
		items.push({
			id: "processing",
			tone: "warning",
			title: "Emision en proceso",
			description: `${processingCount} participante(s) siguen en ejecucion.`,
			pill: "En proceso",
			pillVariant: "warning",
		});
	}

	if (!summary?.canEditConfiguration && summary?.blockEditReasonMessage) {
		items.push({
			id: "edit-blocked",
			tone: "neutral",
			title: "Edicion bloqueada",
			description: summary.blockEditReasonMessage,
			pill: "Bloqueada",
			pillVariant: "neutral",
		});
	}

	return items;
};

const sortStudentsByOperationalPriority = students =>
	[...(students || [])].sort((left, right) => {
		const leftPriority =
			STUDENT_STATUS_PRIORITY[left?.certificateStatus] ?? 99;
		const rightPriority =
			STUDENT_STATUS_PRIORITY[right?.certificateStatus] ?? 99;
		if (leftPriority !== rightPriority) return leftPriority - rightPriority;
		return (left?.fullName || "").localeCompare(right?.fullName || "", "es");
	});

const POSITION_ORDER = {
	LEFT: 0,
	CENTER: 1,
	RIGHT: 2,
};

const parseConfigSummary = config => {
	const normalizedSignatures = (config?.signatures || [])
		.filter(signature => Boolean(signature?.signatureTypeCode))
		.map((signature, index) => ({
			slot: `Firma ${index + 1}`,
			position:
				POSITION_LABELS[signature?.positionCode] ||
				signature?.positionCode ||
				"PEND",
			positionCode: signature?.positionCode || "",
			type: getCertificationSignatureTypeName(signature?.signatureTypeCode),
			name: signature?.fullName || "",
		}))
		.sort((left, right) => {
			const leftOrder = POSITION_ORDER[left.positionCode] ?? 99;
			const rightOrder = POSITION_ORDER[right.positionCode] ?? 99;
			return leftOrder - rightOrder;
		});

	return {
		meta: {
			signatureCount: config?.signatureCount || normalizedSignatures.length || 0,
		},
		signatures: normalizedSignatures,
	};
};

const PastEventCertificates = ({
	eventId,
	autoCertificate,
	organizerCampusName = "",
	step2Data,
	eventEndDate = 0,
	certificationSignatureTypes = [],
	loadingRequest,
}) => {
	const [summary, setSummary] = useState(null);
	const [config, setConfig] = useState(autoCertificate || null);
	const [editorOpen, setEditorOpen] = useState(false);
	const [students, setStudents] = useState([]);
	const [studentsLoading, setStudentsLoading] = useState(false);
	const [globalActionBusy, setGlobalActionBusy] = useState(false);
	const [pdfLoadingStudentId, setPdfLoadingStudentId] = useState(null);
	const [batches, setBatches] = useState([]);
	const [batchesLoading, setBatchesLoading] = useState(false);
	const [batchesLoaded, setBatchesLoaded] = useState(false);
	const [pagination, setPagination] = useState({
		page: 1,
		pageSize: 20,
		total: 0,
	});
	const [statusFilter, setStatusFilter] = useState("");
	const [search, setSearch] = useState("");

	const loadStudentsPage = async ({
		nextStatus = statusFilter,
		nextSearch = search,
		page = 1,
		pageSize = pagination.pageSize,
	} = {}) =>
		requestGetPastEventCertificationStudents(eventId, {
			status: nextStatus,
			search: nextSearch,
			page,
			pageSize,
		});

	const fetchSummaryAndConfig = async () => {
		const [nextSummary, nextConfig] = await Promise.all([
			requestGetPastEventCertificationSummary(eventId),
			requestGetPastEventCertificationConfig(eventId),
		]);
		setSummary(nextSummary);
		setConfig(nextConfig || autoCertificate || null);
	};

	const fillBatches = async () => {
		setBatchesLoading(true);
		try {
			const nextBatches = await requestGetPastEventCertificationBatches(eventId);
			setBatches(nextBatches || []);
			setBatchesLoaded(true);
		} finally {
			setBatchesLoading(false);
		}
	};

	const fetchStudents = async ({
		nextStatus = statusFilter,
		nextSearch = search,
		page = 1,
	} = {}) => {
		setStudentsLoading(true);
		try {
			const response = await loadStudentsPage({
				nextStatus,
				nextSearch,
				page,
			});
			const total = Number(response?.total) || 0;
			const resolvedPageSize =
				Number(response?.pageSize) || pagination.pageSize;
			const responsePage = Number(response?.page) || page;
			const responseItems = response?.items || [];

			if (responseItems.length === 0 && total > 0 && responsePage > 1) {
				const fallbackPage = Math.max(
					1,
					Math.ceil(total / resolvedPageSize)
				);
				const fallbackResponse = await loadStudentsPage({
					nextStatus,
					nextSearch,
					page: fallbackPage,
					pageSize: resolvedPageSize,
				});
				const fallbackItems = fallbackResponse?.items || [];
				setStudents(sortStudentsByOperationalPriority(fallbackItems));
				setPagination({
					page: Number(fallbackResponse?.page) || fallbackPage,
					pageSize:
						Number(fallbackResponse?.pageSize) || resolvedPageSize,
					total: Number(fallbackResponse?.total) || total,
				});
				return;
			}

			setStudents(sortStudentsByOperationalPriority(responseItems));
			setPagination({
				page: responsePage,
				pageSize: resolvedPageSize,
				total,
			});
		} finally {
			setStudentsLoading(false);
		}
	};

	const initializeDashboard = async () => {
		loadingRequest.show("Cargando constancias...");
		try {
			await Promise.all([
				fetchSummaryAndConfig(),
				fetchStudents({ page: 1, nextStatus: "", nextSearch: "" }),
			]);
		} finally {
			loadingRequest.hide();
		}
	};

	useEffect(() => {
		initializeDashboard();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventId]);

	const handleFilterChange = async nextStatus => {
		setStatusFilter(nextStatus);
		await fetchStudents({ nextStatus, page: 1 });
	};

	const handleSearchSubmit = async () => {
		await fetchStudents({ page: 1 });
	};

	const handlePageChange = async nextPage => {
		const totalPages = Math.max(
			1,
			Math.ceil(pagination.total / pagination.pageSize)
		);
		if (
			nextPage < 1 ||
			nextPage > totalPages ||
			nextPage === pagination.page
		) {
			return;
		}
		await fetchStudents({ page: nextPage });
	};

	const handleViewPdf = async studentId => {
		setPdfLoadingStudentId(studentId);
		try {
			const file = await requestGetPastEventCertificationFile(
				eventId,
				studentId
			);
			if (file?.resourceUrl) {
				window.open(file.resourceUrl, "_blank", "noopener,noreferrer");
			}
		} finally {
			setPdfLoadingStudentId(null);
		}
	};

	const handleGlobalReprocess = async () => {
		setGlobalActionBusy(true);
		loadingRequest.show("Programando reproceso...");
		try {
			await requestPastEventCertificationGlobalReprocess(eventId);
			await fetchSummaryAndConfig();
			if (batchesLoaded) await fillBatches();
			await fetchStudents({ page: pagination.page });
		} finally {
			loadingRequest.hide();
			setGlobalActionBusy(false);
		}
	};

	const handleStudentReprocess = async studentId => {
		setGlobalActionBusy(true);
		loadingRequest.show("Programando reproceso...");
		try {
			await requestPastEventCertificationStudentReprocess(eventId, studentId);
			await fetchSummaryAndConfig();
			if (batchesLoaded) await fillBatches();
			await fetchStudents({ page: pagination.page });
		} finally {
			loadingRequest.hide();
			setGlobalActionBusy(false);
		}
	};

	const configSummary = useMemo(
		() => parseConfigSummary(config),
		[config]
	);

	const resolvedOrganizerCampusName =
		organizerCampusName || config?.campusName || "";

	const metrics = useMemo(
		() => [
			{
				key: "issued",
				label: "Emitidos",
				value: summary?.counters?.issued || 0,
			},
			{
				key: "pending",
				label: "Pendientes",
				value: summary?.counters?.pending || 0,
			},
			{
				key: "failed",
				label: "Con error",
				value: summary?.counters?.failed || 0,
			},
			{
				key: "enrolled",
				label: "Inscritos",
				value: summary?.counters?.totalEnrolled || 0,
			},
			{
				key: "notEligible",
				label: "No elegibles",
				value: summary?.counters?.notEligible || 0,
			},
		],
		[summary]
	);

	const attentionItems = useMemo(
		() => buildAttentionItems(summary),
		[summary]
	);

	const reprocessState = useMemo(() => getReprocessState(summary), [summary]);

	const totalPages = Math.max(
		1,
		Math.ceil(pagination.total / pagination.pageSize)
	);

	return (
		<SectionShell>
			<DashboardCard gap="12px">
				<OperationalHeader
					currentBatchStatusText={
						summary?.currentBatch?.status
							? getStatusText(summary.currentBatch.status)
							: ""
					}
					currentBatchStatusVariant={getStatusVariant(
						summary?.currentBatch?.status
					)}
					lastExecutionText={getLastExecutionText(summary)}
					canEditConfiguration={summary?.canEditConfiguration}
					canReprocessGlobal={
						summary?.canReprocessGlobal &&
						!reprocessState.isActiveGlobalReprocess
					}
					isBusy={globalActionBusy}
					onEditCertification={() => setEditorOpen(true)}
					onGlobalReprocess={handleGlobalReprocess}
				/>

				<MetricsStrip metrics={metrics} getMetricTone={getMetricTone} />

				<TopSecondaryStack>
					{attentionItems.length > 0 ? (
						<AttentionPanel items={attentionItems} />
					) : null}

					<ConfigSnapshot
						enabled={config?.enabled}
						revision={getCurrentConfigRevision(summary)}
						organizerCampusName={resolvedOrganizerCampusName}
						signatureCount={configSummary.meta.signatureCount || 0}
						signatures={configSummary.signatures}
					/>
				</TopSecondaryStack>
			</DashboardCard>

			<StudentsSection
				search={search}
				onSearchChange={event => setSearch(event.target.value)}
				onSearchSubmit={handleSearchSubmit}
				isLoading={studentsLoading}
				isBusy={globalActionBusy || studentsLoading}
				pdfLoadingStudentId={pdfLoadingStudentId}
				filters={STUDENT_FILTERS}
				statusFilter={statusFilter}
				onFilterChange={handleFilterChange}
				getFilterCount={getFilterCount}
				summaryCounters={summary?.counters}
				students={students}
				currentPage={pagination.page}
				pageSize={pagination.pageSize}
				total={pagination.total}
				totalPages={totalPages}
				onPageChange={handlePageChange}
				isPaginationDisabled={globalActionBusy || studentsLoading}
				hasPendingReprocess={reprocessState.isActiveGlobalReprocess}
				formatDate={formatDate}
				getStatusText={getStatusText}
				getStatusVariant={getStatusVariant}
				getEligibilityText={getEligibilityText}
				getEligibilityVariant={getEligibilityVariant}
				getErrorText={getErrorText}
				onViewPdf={handleViewPdf}
				onStudentReprocess={handleStudentReprocess}
			/>

			<BatchAuditSection
				batches={batches}
				isLoading={batchesLoading}
				isBusy={globalActionBusy}
				hasLoaded={batchesLoaded}
				onLoadBatches={fillBatches}
				formatDate={formatDate}
				getStatusText={getStatusText}
				getStatusVariant={getStatusVariant}
				getBatchTypeText={getBatchTypeText}
			/>

			<PastEventCertificationEditorDialog
				show={editorOpen}
				onClose={() => setEditorOpen(false)}
				eventId={eventId}
				step2Data={step2Data}
				eventEndDate={eventEndDate}
				currentAutoCertificate={config || autoCertificate}
				organizerCampusName={resolvedOrganizerCampusName}
				certificationSignatureTypes={certificationSignatureTypes}
				loadingRequest={loadingRequest}
				onSaved={async () => {
					await fetchSummaryAndConfig();
					if (batchesLoaded) await fillBatches();
					await fetchStudents({ page: pagination.page });
				}}
			/>
		</SectionShell>
	);
};

export default PastEventCertificates;
